// Homepage previews only. Purchased course playback uses its existing player.
export function mountPreviewVideo(video: HTMLVideoElement, src: string) {
  let visible = false;
  let wantsPlayback = true;
  let userStarted = false;
  let disposed = false;
  let pendingPlay = false;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shouldPlay = () =>
    !disposed && visible && !document.hidden && wantsPlayback && (!motion.matches || userStarted);

  const sync = () => {
    if (!shouldPlay()) {
      video.pause();
      return;
    }
    // No src before this preview is visible: hidden slides cannot start downloads.
    if (!video.getAttribute('src')) video.setAttribute('src', src);
    if (video.paused && !pendingPlay) {
      pendingPlay = true;
      void video
        .play()
        .then(() => {
          // A pending play may resolve after a swipe, tab switch or manual pause.
          if (!shouldPlay()) video.pause();
        })
        .catch(() => {
          // Autoplay rejection is normal; the visible button remains available.
        })
        .finally(() => {
          pendingPlay = false;
        });
    }
  };
  const observer =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          entries => {
            // One observed element can produce several queued threshold changes.
            // Honor its latest state, not an earlier visible entry in this batch.
            const entry = entries[entries.length - 1];
            if (!entry) return;
            visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
            sync();
          },
          { threshold: [0, 0.15] },
        );
  observer?.observe(video);
  if (!observer) visible = true;
  document.addEventListener('visibilitychange', sync);
  motion.addEventListener('change', sync);
  sync();

  return {
    toggle() {
      // Read native state, not an optimistic React flag. A blocked play can retry.
      wantsPlayback = pendingPlay ? !wantsPlayback : video.paused;
      userStarted = wantsPlayback;
      sync();
    },
    dispose() {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', sync);
      motion.removeEventListener('change', sync);
      video.pause();
      video.removeAttribute('src');
      video.load();
    },
  };
}
